/**
 * Dojo TypeScript types
 *
 * See DOCS/howie/DOJO_V1_BUILD_SPEC.md and railway-howie-service/prompts/howie-dojo.md
 * for the source of truth on the round model, beats, and 15-cell profile.
 */

// The six beats of a dojo unit round (plus idle/completed/abandoned terminals).
// Wire values are fixed by DOCS/howie/dojo-units-v1/CONTRACT.md — do not rename.
export type DojoBeat =
  | 'idle'
  | 'intake'
  | 'react_well'
  | 'react_better'
  | 'highlight'
  | 'enact'
  | 'chiddush'
  | 'completed'
  | 'abandoned';

// Beats from the retired 5-beat scenario flow. Old rounds still carry these in the
// database, so the client normalizes them onto the nearest unit beat instead of
// dropping the transition on the floor.
export type LegacyDojoBeat = 'ceremony' | 'react' | 'unpack' | 'reconsider' | 'apply';

// GCL Five Hows dimensions.
export type GCLDimension = 'KNOW' | 'BEHAVE' | 'RELATE' | 'RECOGNIZE' | 'PURSUE';

// Internal levels — NEVER displayed as belts. Used only to calibrate HOWie's pressure.
export type ComplyBehaveOutbehaveLevel = 'comply' | 'behave' | 'outbehave';

// Three coaching voices HOWie can adopt within a round.
export type CoachingVoice = 'socratic' | 'advisory' | 'pattern_interrupt';

// One cell of the 15-cell awareness map.
export interface DimensionCell {
  level: ComplyBehaveOutbehaveLevel;
  confidence: number; // 0.0 – 1.0
  last_engaged: string | null; // ISO date or null
}

// Full 15-cell map: 5 dimensions × 3 levels (one current level per dimension).
export interface FifteenCellMap {
  KNOW: DimensionCell;
  BEHAVE: DimensionCell;
  RELATE: DimensionCell;
  RECOGNIZE: DimensionCell;
  PURSUE: DimensionCell;
}

// Per-dialectic position (matches ONTOLOGY_SPEC.md / migration schema).
export interface DialecticPosition {
  current: number; // 0.0 – 1.0 (where on the spectrum the user currently sits)
  aspirational: number; // where they want to be
  confidence: number;
  last_updated: number; // epoch seconds
}

// Learner tier chosen on first visit. Vignette variants are registered against it.
export type DojoExperienceLevel = 'new_manager' | 'mid_level' | 'senior_executive';

// Full user dojo profile.
export interface DojoProfile {
  fifteen_cell_map: FifteenCellMap;
  dialectic_positions: Record<string, DialecticPosition>;
  commitment_cadence: '1_per_day' | 'weekly' | 'ad_hoc' | null;
  preferred_voice: CoachingVoice | null;
  rounds_completed: number;
  is_first_session: boolean;
  last_chiddush_id: string | null;
  // null until the learner picks a level; the server falls back to mid_level.
  experience_level: DojoExperienceLevel | null;
}

// A single message in a round.
export interface DojoMessage {
  id?: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  beat?: DojoBeat;
  round_id?: string;
}

export type DojoDifficulty = 'foundational' | 'intermediate' | 'advanced' | 'specialized';

export interface DojoCanonicalAnchor {
  canonical_id: string;
  name?: string | null;
  learning_tier?: number | null;
  difficulty?: DojoDifficulty | string | null;
  is_keystone?: boolean;
  confidence?: number;
}

export interface DojoScenarioDag {
  canonical_ids: string[];
  anchors: DojoCanonicalAnchor[];
  learning_tier: number;
  difficulty: DojoDifficulty | string;
  is_keystone: boolean;
  keystone_count: number;
  prerequisites: DojoCanonicalAnchor[];
  upstream_prerequisites?: DojoCanonicalAnchor[];
  missing_prerequisite_ids?: string[];
  covered_prerequisite_ids?: string[];
  selection_reason?: string;
  target_dimension?: GCLDimension;
  source?: 'neo4j' | 'static_dag' | string;
  degraded?: boolean;
}

export interface DojoPedagogicalMapping {
  mapping_version: number;
  review_status: 'testing_ground' | 'curated' | 'approved' | string;
  primary_target: {
    canonical_id: string;
    role: 'primary_target' | string;
    rationale: string;
  };
  supporting_concepts: Array<{
    canonical_id: string;
    role: 'supporting_concept' | string;
    rationale: string;
  }>;
  prerequisite_source: 'computed_from_howie_prerequisite_dag' | string;
  prerequisite_concepts: Array<{
    canonical_id: string;
    role?: string;
    rationale?: string;
  }>;
  diagnostic_signals: {
    comply: string[];
    behave: string[];
    outbehave: string[];
  };
  rubric: {
    again: string;
    hard: string;
    good: string;
    easy: string;
  };
  case_study_sources: Array<{
    kind: string;
    source: string;
    evidence_field: string;
    author?: string;
  }>;
  mapping_notes?: string;
}

export interface DojoScenarioOption {
  scenario_id: string;
  title: string;
  primary_dimension: GCLDimension;
  canonical_id: string | null;
  canonical_name: string | null;
  canonical_ids: string[];
  canonical_names: string[];
  learning_tier?: number | null;
  difficulty?: DojoDifficulty | string | null;
  selection_bucket?: string | null;
  selection_reason?: string | null;
  reason_label?: string | null;
  prerequisites_satisfied?: boolean | null;
  is_recommended?: boolean;
}

export interface DojoDimensionProgress {
  dimension: GCLDimension;
  practice_count: number;
  direct_practice_count?: number;
  supporting_signal_count?: number;
  secondary_signal_count?: number;
  signal_count?: number;
  canonical_count: number;
  strongest_evidence_layer?: string | null;
  strongest_evidence_layer_rank?: number;
  last_practiced_at?: string | null;
  confidence: number;
  has_signal: boolean;
  strongest_cbo_signal?: 'unclear' | 'comply' | 'behave' | 'outbehave' | string;
  latest_evidence_quote?: string | null;
  working_read?: string | null;
  next_edge?: string | null;
  contributing_rounds?: DojoDimensionContribution[];
}

export interface DojoDimensionContribution {
  round_id?: string | null;
  scenario_id?: string | null;
  scenario_title?: string | null;
  relationship?: 'practiced' | 'also_heard' | 'nearby' | string | null;
  evidence_layer?: string | null;
  cbo_signal?: string | null;
  evidence_quote?: string | null;
  created_at?: string | null;
}

export interface DojoPracticeProgress {
  rounds_completed: number;
  completed_canonical_ids: string[];
  due_canonical_ids: string[];
  weak_dimensions: Array<{ dimension: GCLDimension; confidence: number }>;
  dimension_progress?: Partial<Record<GCLDimension, DojoDimensionProgress>>;
  activity?: DojoPracticeActivity[];
}

export interface DojoPracticeActivity {
  practice_context?: string;
  round_id: string;
  scenario_id: string | null;
  title: string;
  completed_at: string;
  dimensions: GCLDimension[];
}

// A scenario as returned by `get_dojo_scenario` (matches DOJO_SCENARIOS.md §10 schema).
export interface DojoScenario {
  scenario_id: string;
  title: string;
  round_type: 'diagnostic' | 'starter' | 'generated' | 'user_brought';
  primary_dimension: GCLDimension;
  secondary_dimensions: GCLDimension[];
  primary_dialectic: string;
  scaffolding_framework: string | null;
  react_prompt: string;
  unpack_quote: {
    text: string;
    author: string;
    source: string;
    canonical_id?: string;
  };
  kg_anchors: {
    canonical_ids: string[];
    anecdote_id: string | null;
    dialectic_id: string | null;
  };
  pedagogical_mapping?: DojoPedagogicalMapping;
  listening_for: {
    comply_signals: string[];
    behave_signals: string[];
    outbehave_signals: string[];
  };
  reconsider_prompt?: string;
  apply_prompt?: string;
  estimated_minutes: number;
  dag?: DojoScenarioDag;
  _target_dimension?: GCLDimension;
  _recent_scenario_ids?: string[];
  _dag_selection?: {
    target_dimension?: GCLDimension;
    target_override?: boolean;
    recent_scenario_ids?: string[];
    completed_canonical_ids?: string[];
    candidates?: Array<{
      scenario_id: string;
      title: string;
      primary_dimension: GCLDimension;
      learning_tier?: number;
      difficulty?: DojoDifficulty | string;
      missing_prerequisite_ids?: string[];
      selection_reason?: string;
    }>;
    degraded?: boolean;
  };
}

// ---------------------------------------------------------------------------
// Units (V1 dojo experience) — shapes fixed by DOCS/howie/dojo-units-v1/CONTRACT.md
// ---------------------------------------------------------------------------

// Green = what the leader did well. Yellow = what merely got the job done.
export type DojoHighlightKind = 'admirable' | 'adequate';

// The ANSWER KEY. Authored spans arrive with the unit but are never painted onto
// the reading surface before the learner has marked it themselves — they exist to
// judge and coach what the learner marked and missed.
export interface DojoHighlightSpan {
  id: string;
  paragraph: number;
  // Exact substring of exemplar paragraph `paragraph`. Seed-time validated.
  quote: string;
  kind: DojoHighlightKind;
  note: string;
}

// A passage the learner marked themselves. `start`/`end` are offsets into the
// ORIGINAL text of `paragraph` — the rendered DOM is rebuilt from them, so they
// are the only durable reference. Marks never overlap each other.
export interface DojoLearnerMark {
  id: string;
  paragraph: number;
  start: number;
  end: number;
  quote: string;
  kind: DojoHighlightKind;
}

export interface DojoUnitReactPrompts {
  well: string;
  better: string;
}

export interface DojoUnitExemplar {
  leader_name: string;
  paragraphs: string[];
  react_prompts: DojoUnitReactPrompts;
  // No `highlights` field: the authored answer key never travels to the client.
  // It stays server-side and informs Howie's coaching only — see
  // build_unit_client_payload in howie_dojo_tools.py.
}

export interface DojoUnitEnact {
  paragraphs: string[];
  question: string;
  flip_axis: string;
}

// The client-facing unit payload. Reviewer-only material (rubric, listening_for,
// design tags beyond flip_axis) never leaves the server.
export interface DojoUnit {
  scenario_id: string;
  round_type: 'unit';
  title: string;
  imperative_id: string;
  imperative_label: string;
  primary_dimension: GCLDimension;
  level: DojoExperienceLevel;
  estimated_minutes: number;
  intake_questions: string[];
  exemplar: DojoUnitExemplar;
  enact: DojoUnitEnact;
}

export type DojoUnitStatus = 'completed' | 'available' | 'recommended' | 'in_progress';

export type DojoUnitRating = 'again' | 'hard' | 'good' | 'easy';

export interface DojoImperativeSummary {
  imperative_id: string;
  imperative_label: string;
  order: number;
  units_total: number;
  units_completed: number;
}

export interface DojoUnitSummary {
  unit_id: string;
  imperative_id: string;
  /** Authored dimension from the catalog; older responses may omit it. */
  primary_dimension?: GCLDimension | null;
  title: string;
  status: DojoUnitStatus;
  latest_unit_rating?: DojoUnitRating | string | null;
  last_practiced_at?: string | null;
  /** The learner's most recent completed round on this unit, if any — opens read-only via GET /api/dojo/rounds/:id. */
  latest_round_id?: string | null;
}

export interface DojoMasteryCounts {
  new: number;
  learning: number;
  review: number;
  mastered: number;
}

export interface DojoMastery {
  counts: DojoMasteryCounts;
  // Per-dimension mastery tallies when the judge has produced them.
  by_dimension?: Partial<Record<GCLDimension, Partial<DojoMasteryCounts>>>;
}

// GET /api/dojo/units
export interface DojoUnitsCatalog {
  profile: DojoProfile | null;
  imperatives: DojoImperativeSummary[];
  units: DojoUnitSummary[];
  recommended_unit_id: string | null;
  recommended_reason?: 'review_due' | 'progression' | 'catalog_default' | string | null;
  progress: DojoPracticeProgress | null;
  mastery: DojoMastery | null;
  rounds_completed: number;
}

export interface DojoImperativeProgress {
  imperative_id: string;
  units_completed: number;
  units_total: number;
}

// How well the learner read one authored span, judged server-side.
export interface DojoSpanEvidence {
  id: string;
  kind: 'admirable' | 'adequate';
  found: boolean;
  found_in?: 'react_well' | 'react_better' | null;
  verbatim_evidence?: string | null;
  confidence?: number | null;
  credit?: number;
}

// How much of the exemplar the learner noticed on their own.
export interface DojoNoticing {
  score?: number | null;
  spotted_before_reveal?: DojoSpottedRung | null;
  cost_named?: boolean;
  admirable_found?: number;
  admirable_total?: number;
  adequate_found?: number;
  adequate_total?: number;
}

export type DojoSpottedRung =
  | 'none'
  | 'one_yellow'
  | 'multiple_yellows'
  | 'yellow_plus_cost';

// What the judge leaves behind on the round. These are the keys the server
// actually emits — the earlier headline/one_liner/summary/noticed guesses never
// matched anything, so the close screen's read has never rendered.
export interface DojoJudgeSummary {
  unit_rating?: 'again' | 'hard' | 'good' | 'easy' | null;
  enact_read?: string | null;
  coaching_focus?: string | null;
  spans_found?: string[];
  spans_missed?: string[];
  span_evidence?: DojoSpanEvidence[];
  noticing_score?: number | null;
  spotted_before_reveal?: DojoSpottedRung | null;
  noticing?: DojoNoticing | null;
  fallback?: boolean;
  assessor?: string | null;
  rubric_version?: string | null;
  judged_at?: string | null;
}

// GET /api/dojo/rounds/:round_id — read-only view of one of the learner's own
// rounds. Deliberately smaller than DojoJudgeSummary: the server strips
// everything derived from the answer key before this ever reaches the wire.
export type DojoRoundStatus = 'completed' | 'in_progress' | 'abandoned';

export interface DojoRoundReviewSummary {
  unit_rating?: DojoUnitRating | null;
  coaching_focus?: string | null;
  enact_read?: string | null;
  judged_at?: string | null;
}

export interface DojoRoundReview {
  success: true;
  round_id: string;
  status: DojoRoundStatus;
  beat: DojoBeat;
  started_at: string | null;
  completed_at: string | null;
  unit: DojoUnit;
  intake_answers: { challenging?: string | null; natural?: string | null };
  marks: DojoLearnerMark[];
  messages: DojoMessage[];
  /** The learner's own words from the Insight beat, resolved from chiddush_id.
   * Null for a round abandoned or still in progress before that beat. */
  insight: string | null;
  judge_summary: DojoRoundReviewSummary;
}

// Saved insight record.
export interface Insight {
  id: string;
  user_id: string;
  round_id: string;
  text: string;
  kg_metadata: {
    dimension: GCLDimension;
    dialectic_id: string | null;
    canonical_ids: string[];
    scenario_id: string;
    voice_used: CoachingVoice | null;
    anecdote_id: string | null;
    scenario_learning_tier?: number | null;
    scenario_difficulty?: DojoDifficulty | string | null;
    dag_selection_metadata?: Record<string, unknown>;
  };
  share_permission: boolean;
  created_at: string;
}

export interface UseHowieDojoWebSocketProps {
  userId: string | undefined;
  onBeatChange?: (beat: DojoBeat) => void;
  onRoundComplete?: (roundId: string, insightId: string | null) => void;
  onProfileUpdate?: (profile: DojoProfile) => void;
}

export interface DojoRoundCompletion {
  roundId: string;
  insightId: string | null;
  imperativeProgress: DojoImperativeProgress | null;
  judgeSummary: DojoJudgeSummary | null;
}

export interface UseHowieDojoWebSocketReturn {
  // State
  messages: DojoMessage[];
  currentBeat: DojoBeat;
  currentRoundId: string | null;
  currentUnit: DojoUnit | null;
  // The learner's own marks on the exemplar, rehydrated on resume.
  learnerMarks: DojoLearnerMark[];
  setLearnerMarks: (marks: DojoLearnerMark[]) => void;
  currentScenario: DojoScenario | null;
  catalog: DojoUnitsCatalog | null;
  practiceProgress: DojoPracticeProgress | null;
  profile: DojoProfile | null;
  lastCompletion: DojoRoundCompletion | null;
  streamingText: string;
  draft: string;
  setDraft: (value: string) => void;
  clearDraft: () => void;
  // Status
  isLoading: boolean;
  isLoadingCatalog: boolean;
  isSending: boolean;
  isStreaming: boolean;
  canSteer: boolean;
  hasError: boolean;
  error: string | null;
  catalogError: string | null;
  connectionStatus: 'connecting' | 'connected' | 'disconnected' | 'error';
  // Actions
  startRound: (targetDimension?: GCLDimension, scenarioId?: string) => Promise<void>;
  sendMessage: (message: string) => Promise<void>;
  submitIntake: (answers: DojoIntakeAnswers) => Promise<void>;
  submitMarks: (marks: DojoLearnerMark[]) => Promise<void>;
  advanceBeat: (toBeat: DojoBeat) => Promise<void>;
  setExperienceLevel: (level: DojoExperienceLevel) => Promise<void>;
  saveInsight: (text: string, sharePermission?: boolean) => Promise<void>;
  endRoundEarly: () => Promise<void>;
  clearRound: () => void;
  retryLastMessage: () => Promise<void>;
  refreshCatalog: () => Promise<void>;
}

export interface DojoIntakeAnswers {
  challenging: string;
  natural: string;
}

// Display helpers — public-facing language. NEVER expose internal level labels.
export const BEAT_DISPLAY: Record<DojoBeat, string> = {
  idle: 'Ready',
  intake: 'Arrive',
  // The learner marks the vignette themselves here, on first read. The authored
  // key never reaches them.
  highlight: 'Mark',
  react_well: 'Notice',
  react_better: 'Sharpen',
  enact: 'Your Move',
  chiddush: 'Insight',
  completed: 'Complete',
  abandoned: 'Ended',
};

// The six beats the stepper walks through, in order.
export const UNIT_BEAT_SEQUENCE: DojoBeat[] = [
  'intake',
  'highlight',
  'react_well',
  'react_better',
  'enact',
  'chiddush',
];

export const EXPERIENCE_LEVEL_DISPLAY: Record<DojoExperienceLevel, string> = {
  new_manager: 'New manager',
  mid_level: 'Mid-level leader',
  senior_executive: 'Senior / executive',
};

// Personal-read phrasing over the exact GCL Matrix Five Hows.
export const DIMENSION_DISPLAY: Record<GCLDimension, string> = {
  KNOW: 'HOW YOU KNOW',
  BEHAVE: 'HOW YOU BEHAVE',
  RELATE: 'HOW YOU RELATE',
  RECOGNIZE: 'HOW YOU RECOGNIZE',
  PURSUE: 'HOW YOU PURSUE',
};

// Dov's framework subtitles per tier (May 26 2026).
export const TIER_SUBTITLE: Record<ComplyBehaveOutbehaveLevel, string> = {
  comply: 'Command & Control',
  behave: 'Carrots & Sticks',
  outbehave: 'Self-Governance',
};

// Internal medal label — NEVER shown as a rank/score. Only used for muted UI accents.
// Per Dov (2025-11-20): "how is not a status."
export const TIER_MEDAL: Record<ComplyBehaveOutbehaveLevel, '🥉' | '🥈' | '🥇'> = {
  comply: '🥉',
  behave: '🥈',
  outbehave: '🥇',
};

// Awareness descriptors — what the user sees instead of "level/belt/rank."
// Per Dov (2025-11-20): "how is not a status."
export const LEVEL_AWARENESS_DESCRIPTOR: Record<ComplyBehaveOutbehaveLevel, string> = {
  comply: 'You default to external cues',
  behave: 'You weigh trade-offs',
  outbehave: 'You move from values',
};
